import { useCallback, useRef } from "react";

import {
  EMPTY_HOVER_LEGEND,
  useHoverHighlightClass,
  useHoverLegendWriter
} from "atoms/interaction";
import classnames from "classnames";
import styled from "styled-components";
import { removeProbitInfoFromtitle } from "utils/removeProbitInfoFromTitle";

import { useChartState } from "../context";

const ChartLegendItem = ({ legend, useCount, ...rest }) => {
  const scheduleHoverWrite = useHoverLegendWriter();
  const { screenshot } = useChartState();

  const wrapperRef = useRef<HTMLDivElement>(null);

  const transformTitle = useCallback((t: string) => removeProbitInfoFromtitle(t), []);
  useHoverHighlightClass(wrapperRef, legend.title, legend.id, transformTitle);

  const fontSize = screenshot.visible
    ? Number(screenshot.preset?.legendFontSize) || 14
    : 14;

  // events
  const handleMouseOver = () => {
    if (screenshot.visible) return;
    scheduleHoverWrite({
      item: legend.title,
      id: legend.id,
      group: "",
      itemUnfiltered: "",
      isMultiProductChart: false
    });
  };

  const handleMouseOut = () => {
    if (screenshot.visible) return;
    scheduleHoverWrite(EMPTY_HOVER_LEGEND);
  };

  // derived class names
  const wrapperClassNames = classnames({
    "screenshot-mode": screenshot.visible
  });

  return (
    <Wrapper
      ref={wrapperRef}
      data-testid="chart-legend-item-wrapper"
      className={wrapperClassNames}
      onPointerEnter={handleMouseOver}
      onPointerLeave={handleMouseOut}
      {...rest}>
      <Indicator data-testid="chart-legend-item-color-indicator" color={legend.color} />
      <Title fontSize={fontSize}>{useCount ? legend.title : legend.value}</Title>
    </Wrapper>
  );
};

export default ChartLegendItem;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: transparent;
  backface-visibility: hidden;

  &.highlighted,
  &:hover {
    background-color: #dbdbdb;
  }

  // Don't display legend item backgrounds on hover & active in screenshot mode
  &.screenshot-mode.highlighted,
  &.screenshot-mode:hover {
    background-color: transparent;
  }
`;

const Indicator = styled.div`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 2px;
  background-color: ${(props) => props.color};
`;

const Title = styled.div`
  font-size: ${(props) => props.fontSize}px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: none;
`;
